/**
 * Builds review summary lines for multi-select preference groups.
 *
 * Belongs here: review copy from answers.
 * Does not belong here: UI components.
 */
import type { PreferenceQuestionGroup } from '../preferences/setup/types'
import type { PreferencesAnswersMap } from '../preferences/setup/types'
import type { QuestionnaireQuestionApi } from '../shared/types/questionnaire.types'
import { evaluateCondition } from './evaluateCondition'
import { formatRupeeLabel } from '../../plans-v4/display-values/lumpsumPlanAmount'

function formatValueForReview(
  questionId: string,
  value: string | number | string[] | undefined,
  questions: QuestionnaireQuestionApi[]
): string {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value)) {
    return formatRupeeLabel({ rawAmount: value })
  }
  const optionId = Array.isArray(value) ? value[0] : value
  const question = questions.find((q) => q.id === questionId)
  const option = question?.options?.find((opt) => opt.id === optionId)
  return option?.displayText ?? String(optionId ?? value)
}

function formatFundCountPrefix(fundCount: number): string {
  return fundCount === 1 ? '1 fund' : `${fundCount} funds`
}

export function groupHasMultiSelectReview(group: PreferenceQuestionGroup | undefined): boolean {
  return (group?.questions ?? []).some((question) => question?.input?.type === 'MULTI_SELECT_GROUPS')
}

function getReviewLineForItem(
  trimmed: string,
  showFundCount: boolean | null | undefined,
  fundCount: number | undefined
): string | null {
  // API sends boolean | null — only true enables the fund-count prefix
  const showCount = showFundCount === true && fundCount != null && Number.isFinite(fundCount) && fundCount > 0
  if (showCount) {
    return trimmed ? `${formatFundCountPrefix(fundCount)} + ${trimmed}` : formatFundCountPrefix(fundCount)
  }
  return trimmed || null
}

function getMultiSelectGroupsReviewLines(
  group: PreferenceQuestionGroup,
  answers: PreferencesAnswersMap
): string[] {
  const questions = group.questions ?? []
  let selectedCategoryCount = 0
  let unselectedCategoryCount = 0
  let hasMultiSelectGroups = false

  for (const question of questions) {
    if (question?.input?.type !== 'MULTI_SELECT_GROUPS') continue
    hasMultiSelectGroups = true
    const selectedIds = answers[question.id]
    const selectedSet = new Set(Array.isArray(selectedIds) ? selectedIds : [])
    for (const option of question.options ?? []) {
      if (selectedSet.has(option.id)) {
        selectedCategoryCount += 1
      } else {
        unselectedCategoryCount += 1
      }
    }
  }

  if (!hasMultiSelectGroups) return []

  const lines: string[] = []
  if (selectedCategoryCount > 0) {
    lines.push(
      selectedCategoryCount === 1
        ? '1 category to reallocate'
        : `${selectedCategoryCount} categories to reallocate`
    )
  }
  if (unselectedCategoryCount > 0) {
    lines.push(
      unselectedCategoryCount === 1
        ? '1 category to keep unchanged'
        : `${unselectedCategoryCount} categories to keep unchanged`
    )
  }
  return lines
}

/**
 * Returns review summary lines for a preference group using its reviewResolver.
 * For each resolver item whose reviewCondition matches answers, adds one line
 * with reviewCopy after replacing each %s with the value for the corresponding questionId.
 * When item.showFundCount is true and fundCount is provided and > 0, the line is shown as
 * "{n} fund(s) + {resolvedReviewCopy}" (e.g. "1 fund + 10% of portfolio"). fundCount 0 is not shown.
 */
export function getGroupReviewSummaryLines(
  group: PreferenceQuestionGroup,
  answers: PreferencesAnswersMap,
  fundCount?: number
): string[] {
  if (groupHasMultiSelectReview(group)) {
    return getMultiSelectGroupsReviewLines(group, answers)
  }

  const resolver = group.reviewResolver
  if (!resolver?.length) return []

  const lines: string[] = []
  const answersMap = answers ?? {}
  const questions = group.questions ?? []

  for (const item of resolver) {
    if (!evaluateCondition(item.reviewCondition, answersMap)) continue
    let text = item.reviewCopy ?? ''
    if (text.includes('%s') && item.questionId != null) {
      const value = formatValueForReview(item.questionId, answersMap[item.questionId], questions)
      text = text.replace('%s', value)
    }
    const trimmed = text.trim()
    const line = getReviewLineForItem(trimmed, item.showFundCount, fundCount)
    if (line != null) lines.push(line)
  }

  return lines
}
